import { useState, useEffect } from "react";
import { MonthView, MONTHS } from "@/components/MonthView";
import { WeekView } from "@/components/WeekView";
import { DayTimeline } from "@/components/DayTimeline";
import { MindMap } from "@/components/MindMap";
import { useLocation } from "react-router-dom";
import { DuplicateDayDialog } from "@/components/DuplicateDayDialog";
import { DuplicateMonthDialog } from "@/components/DuplicateMonthDialog";
import { useUI } from "@/context/UIContext";
import { useApp } from "@/context/AppContext";
import { ChevronLeft, ChevronRight, Copy } from "lucide-react";
import { strToDate, dateToStr, todayStr } from "@/lib/timeUtils";
import { AnimatePresence, motion } from "framer-motion";

const DOW_FULL = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const VIEWS = [{ v: "month", l: "Month" }, { v: "week", l: "Week" }, { v: "day", l: "Day" }, { v: "mindmap", l: "Mind Map" }];

export function CalendarPage() {
  const { openEventModal } = useUI();
  const { settings } = useApp();
  const [view, setView] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get("view") === "mindmap" ? "mindmap" : "month";
    } catch (e) {
      return "month";
    }
  });
  const [cursor, setCursor] = useState(new Date());
  const [showDup, setShowDup] = useState(false);
  const [showDupMonth, setShowDupMonth] = useState(false);
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("view") === "mindmap") setView("mindmap");
  }, [location.search]);

  const nav = (dir) => {
    const d = new Date(cursor);
    if (view === "month") d.setMonth(d.getMonth() + dir);
    else if (view === "week") d.setDate(d.getDate() + dir * 7);
    else d.setDate(d.getDate() + dir); // day & mindmap
    setCursor(d);
  };
  const goToday = () => setCursor(new Date());
  const selectDay = (ds) => { setCursor(strToDate(ds)); setView("day"); };
  const editEvent = (ev) => openEventModal(ev);
  const createAt = (min) => openEventModal({ date: dateToStr(cursor), start_minutes: min, end_minutes: Math.min(1440, min + 60) });

  const cursorStr = dateToStr(cursor);
  const title = (view === "day" || view === "mindmap")
    ? `${DOW_FULL[cursor.getDay()]}, ${MONTHS[cursor.getMonth()]} ${cursor.getDate()}`
    : `${MONTHS[cursor.getMonth()]} ${cursor.getFullYear()}`;

  return (
    <div className="h-full flex flex-col p-4 lg:p-6">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-1">
          <button onClick={() => nav(-1)} className="w-9 h-9 rounded-lg flex items-center justify-center hover:opacity-80" style={{ background: "var(--bg-card)", color: "var(--text-secondary)" }} data-testid="button-prev-date"><ChevronLeft size={18} /></button>
          <button onClick={() => nav(1)} className="w-9 h-9 rounded-lg flex items-center justify-center hover:opacity-80" style={{ background: "var(--bg-card)", color: "var(--text-secondary)" }} data-testid="button-next-date"><ChevronRight size={18} /></button>
        </div>
        <button onClick={goToday} className="h-9 px-4 rounded-lg text-sm font-medium hover:opacity-80" style={{ background: "var(--bg-card)", color: "var(--text-primary)", border: "1px solid var(--border-subtle)" }} data-testid="button-today-nav">Today</button>
        {(view === "day" || view === "mindmap") && (
          <button onClick={() => setShowDup(true)} className="h-9 px-3.5 rounded-lg text-sm font-medium flex items-center gap-1.5 hover:opacity-80" style={{ background: "var(--bg-card)", color: "var(--accent-primary)", border: "1px solid var(--border-active)" }} data-testid="button-copy-day">
            <Copy size={15} /> Copy Day
          </button>
        )}
        {view === "month" && (
          <button onClick={() => setShowDupMonth(true)} className="h-9 px-3.5 rounded-lg text-sm font-medium flex items-center gap-1.5 hover:opacity-80" style={{ background: "var(--bg-card)", color: "var(--accent-primary)", border: "1px solid var(--border-active)" }} data-testid="button-copy-month">
            <Copy size={15} /> Copy Month
          </button>
        )}
        <h2 className="font-display text-xl lg:text-2xl font-bold ml-1" style={{ color: "var(--text-primary)" }} data-testid="calendar-title">{title}</h2>

        <div className="ml-auto flex p-1 rounded-xl" style={{ background: "var(--bg-card)" }}>
          {VIEWS.map((vv) => (
            <button key={vv.v} onClick={() => setView(vv.v)} className="relative px-4 py-1.5 rounded-lg text-sm font-medium transition-colors" style={{ color: view === vv.v ? "#fff" : "var(--text-secondary)" }} data-testid={`view-switch-${vv.v}`}>
              {view === vv.v && <motion.div layoutId="view-pill" className="absolute inset-0 rounded-lg" style={{ background: "var(--accent-primary)" }} transition={{ type: "spring", stiffness: 400, damping: 30 }} />}
              <span className="relative z-10">{vv.l}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 min-h-0 rounded-3xl p-4 lg:p-5" style={{ background: "var(--bg-timeline-track)", border: "1px solid var(--border-subtle)" }}>
        <AnimatePresence mode="wait">
          <motion.div key={view + cursorStr} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.22 }} className="h-full">
            {view === "month" && <MonthView cursor={cursor} onSelectDay={selectDay} />}
            {view === "week" && <WeekView cursor={cursor} onSelectDay={selectDay} onEditEvent={editEvent} />}
            {view === "day" && (
              <div className="h-full max-w-3xl mx-auto">
                <DayTimeline dateStr={cursorStr} onEditEvent={editEvent} onCreateAt={createAt} />
              </div>
            )}
          {view === "mindmap" && (
            <MindMap dateStr={cursorStr} onEditEvent={editEvent} onCreate={() => createAt(540)} />
          )}
          </motion.div>
        </AnimatePresence>
      </div>
      <DuplicateDayDialog open={showDup} onClose={() => setShowDup(false)} sourceDateStr={cursorStr} />
      <DuplicateMonthDialog open={showDupMonth} onClose={() => setShowDupMonth(false)} sourceMonthDate={cursor} />
    </div>
  );
}
